import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './Offers.css';

interface DLabOffer {
  id: number;
  name: string;
  external_campaign_id: string;
  last_synced_at: string | null;
  is_active: boolean;
  payout: number | string;
}

interface DLabStatus {
  success: boolean;
  totalOffers: number;
  lastSync: string | null;
  offers: DLabOffer[];
}

const DLabIntegration: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<DLabStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDLabStatus();
      setStatus(response);
    } catch (error: any) {
      console.error('Error fetching D-LAB status:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to fetch D-LAB status' });
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setMessage(null);
      const response = await apiService.syncDLabCampaigns();

      setMessage({
        type: 'success',
        text: `Sync completed! Created: ${response.created}, Updated: ${response.updated}, Errors: ${response.errors}`
      });

      await fetchStatus();
    } catch (error: any) {
      console.error('Error syncing D-LAB:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to sync D-LAB campaigns' });
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>D-LAB Integration</h1>
        </div>
        <div className="page-empty-state">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="offers-page">
      <div className="page-header">
        <h1>D-LAB Integration</h1>
        <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {message && <div className={`status-banner ${message.type}`}>{message.text}</div>}

      <div className="offers-section">
        <div className="integration-stats">
          <div className="integration-stat">
            <div className="integration-stat-label">Total D-LAB Offers</div>
            <div className="integration-stat-value">{status?.totalOffers || 0}</div>
          </div>
          <div className="integration-stat">
            <div className="integration-stat-label">Last Sync</div>
            <div className="integration-stat-value">{formatDate(status?.lastSync || null)}</div>
          </div>
          <div className="integration-stat">
            <div className="integration-stat-label">Integration Status</div>
            <div className="integration-stat-value integration-stat-success">Active</div>
          </div>
        </div>

        <h2 className="section-title">Synced Campaigns</h2>

        {status && status.offers.length === 0 ? (
          <div className="page-empty-state">
            <p>No campaigns synced yet. Click "Sync Now" to import campaigns from D-LAB.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="offers-table">
              <thead>
                <tr>
                  <th>Offer ID</th>
                  <th>Campaign Name</th>
                  <th>External ID</th>
                  <th>Payout</th>
                  <th>Status</th>
                  <th>Last Synced</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {status?.offers.map((offer) => (
                  <tr key={offer.id}>
                    <td>{offer.id}</td>
                    <td>{offer.name}</td>
                    <td>
                      <code className="inline-code">{offer.external_campaign_id}</code>
                    </td>
                    <td>${typeof offer.payout === 'number' ? offer.payout.toFixed(2) : parseFloat(String(offer.payout)).toFixed(2)}</td>
                    <td>
                      <button className={`status-badge ${offer.is_active ? 'active' : 'inactive'}`} disabled>
                        {offer.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>{formatDate(offer.last_synced_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => navigate(`/offers/view/${offer.id}`)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mapping-panel">
          <h3 className="section-title">D-LAB Macro Mapping</h3>
          <table className="mapping-table">
            <thead>
              <tr>
                <th>Your Parameter</th>
                <th>D-LAB Macro</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code className="inline-code">click_id</code></td>
                <td><code className="inline-code">p_click</code></td>
                <td>Your unique click ID</td>
              </tr>
              <tr>
                <td><code className="inline-code">aff_id</code></td>
                <td><code className="inline-code">sub</code></td>
                <td>Publisher ID</td>
              </tr>
              <tr>
                <td><code className="inline-code">aff_sub3</code></td>
                <td><code className="inline-code">app</code></td>
                <td>Bundle ID / App ID</td>
              </tr>
              <tr>
                <td><code className="inline-code">aff_sub3</code></td>
                <td><code className="inline-code">domain</code></td>
                <td>App name</td>
              </tr>
              <tr>
                <td><code className="inline-code">creative_id</code></td>
                <td><code className="inline-code">creative</code></td>
                <td>Creative ID</td>
              </tr>
              <tr>
                <td><code className="inline-code">gaid</code></td>
                <td><code className="inline-code">gaid</code></td>
                <td>Google Advertising ID</td>
              </tr>
              <tr>
                <td><code className="inline-code">ifaid</code></td>
                <td><code className="inline-code">idfa</code></td>
                <td>iOS IDFA</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DLabIntegration;
